import { Injectable, signal } from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {PreviewDataSourceInfo} from '../../../../data-source/models/preview-data-source-info.model';
import {PreviewColumns} from '../../../../data-source/models/preview-columns.model';
import {skip} from 'rxjs/operators';
import {DataSource} from '../../../../data-source/models/data-source.modol';
import {SubmitType} from '../../../models/submit.type';
import {Connection} from '../../../../main/modules/management/models/Connection.model';
import {ConnectionTables} from '../../../models/connection-tables.model';
import {SqlServerConnection} from '../../../../data-source/models/sql-server-connection.model';
import {OracleConnection} from '../../../../data-source/models/oracle-connection.model';
import {IncrementalSettingModel} from "../../../../data-source/models/incremental-setting.model";

@Injectable({
  providedIn: 'root',
})
export class DataSourcePreviewState {

  addToDashboard: boolean;
  dashboardId: string;

  private workspaceId$ = new BehaviorSubject<string>(null);
  private submitState$ = new BehaviorSubject<SubmitType>('notSubmitted');
  private previewDataSourceInfo$ = new BehaviorSubject<PreviewDataSourceInfo>(null);
  private previewTableContent$ = new BehaviorSubject<PreviewColumns>(null);
  private dataSources$ = new BehaviorSubject<Partial<DataSource>[]>(null);
  private excelSheetNames$ = new BehaviorSubject<string[]>(null);

  private totalPages$ = new BehaviorSubject<number>(null);
  private pageNumber$ = new BehaviorSubject<number>(1);
  private hasError$ = new BehaviorSubject<boolean>(false);
  private connections$ = new BehaviorSubject<Partial<Connection>[]>([]);
  private connectionTables$ = new BehaviorSubject<Partial<ConnectionTables>>(null);
  private sqlServerConnection$ = new BehaviorSubject<Partial<SqlServerConnection>>(null);
  private incrementalSetting$ = new BehaviorSubject<Partial<IncrementalSettingModel>>(null);
  private oracleConnection$ = new BehaviorSubject<Partial<OracleConnection>>(null);
  isDatasourceAdded  = signal<boolean>(false);

  constructor() { }

  updatePreviewDataSourceInfo(dataSource: PreviewDataSourceInfo) {
    this.previewDataSourceInfo$.next(dataSource);
  }

  getPreviewDataSourceInfo$(): Observable<PreviewDataSourceInfo> {
    return  this.previewDataSourceInfo$.asObservable();
  }

  updatePreviewTableContent(columns: PreviewColumns): void {
    this.previewTableContent$.next(columns);
  }

  getPreviewTableContent$(): Observable<PreviewColumns> {
    return this.previewTableContent$.asObservable().pipe(skip(1));
  }

  updateDataSources(dataSources: Partial<DataSource>[]): void {
    this.dataSources$.next(dataSources);
  }

  getDataSources$(): Observable<Partial<DataSource>[]> {
    return this.dataSources$.asObservable().pipe(skip(1));
  }

  // excel
  setExcelSheetNames(sheetNames: string[]): void {
    this.excelSheetNames$.next(sheetNames);
  }

  getExcelSheetNames$(): Observable<string[]> {
    return this.excelSheetNames$.asObservable().pipe(skip(1));
  }

  // defaults
  updateWorkspaceId(workspaceId: string): void {
    this.workspaceId$.next(workspaceId);
  }

  getWorkspaceId$(): Observable<string> {
    return this.workspaceId$.asObservable();
  }

  setSubmitState(submitted: SubmitType): void {
    this.submitState$.next(submitted);
  }

  getSubmitState$(): Observable<SubmitType> {
    return this.submitState$.asObservable();
  }
  setTotalPages(totalPages: number) {
    this.totalPages$.next(totalPages);
  }

  getTotalPages$(): Observable<number> {
    return this.totalPages$.asObservable().pipe(skip(1));
  }

  setPageNumber(pageNumber: number) {
    this.pageNumber$.next(pageNumber);
  }

  getPageNumber$(): Observable<number> {
    return this.pageNumber$.asObservable().pipe(skip(1));
  }

  setAddToDashboard(addToDashboard: boolean) {
    this.addToDashboard = addToDashboard;
  }

  setDashboardId(dashboardId: string) {
    this.dashboardId = dashboardId;
  }

  updateErrorTableContent(hasError: boolean): void {
    this.hasError$.next(hasError);
  }

  getErrorTableContent$(): Observable<boolean> {
    return this.hasError$.asObservable();
  }

  setConnections(connections: Partial<Connection>[]): void {
    this.connections$.next(connections);
  }

  getConnections$(): Observable<Partial<Connection>[]> {
    return this.connections$.asObservable();
  }

  updateConnectionTables(connectionTables: Partial<ConnectionTables>): void {
    this.connectionTables$.next(connectionTables);
  }

  getConnectionTables$(): Observable<Partial<ConnectionTables>> {
    return this.connectionTables$.asObservable();
  }

  updateSqlServerConnection(sqlServerConnection: Partial<SqlServerConnection>): void {
    this.sqlServerConnection$.next(sqlServerConnection);
  }

  getSqlServerConnection$(): Observable<Partial<SqlServerConnection>> {
    return this.sqlServerConnection$.asObservable().pipe(skip(1));
  }

  updateIncrementalSetting(incrementalSettingModel: Partial<IncrementalSettingModel>): void {
    this.incrementalSetting$.next(incrementalSettingModel);
  }

  getIncrementalSetting$(): Observable<Partial<IncrementalSettingModel>> {
    return this.incrementalSetting$.asObservable().pipe(skip(1));
  }

  updateOracleConnection(oracleConnection: Partial<OracleConnection>): void {
    this.oracleConnection$.next(oracleConnection);
  }

  getOracleConnection$(): Observable<Partial<OracleConnection>> {
    return this.oracleConnection$.asObservable().pipe(skip(1));
  }
  setIsDatasourceAdded(value: boolean) {
    this.isDatasourceAdded.set(value);
  }
}
